import UploadArea from '@/components/UploadArea';
import { InputType } from '@/types';
import {
  imageAccept,
  videoAccept,
  imageExtensions,
  videoExtensions,
} from '@/consts/configs';
import getUploadTitle from '@/utils/ui/getUploadTitle';
import useStyles from './styles';
import type { MediaInputsGridProps } from './types';

/**
 * Responsive grid of media inputs. Renders nothing when there are no
 * media inputs — the empty check lives inside the component so callers
 * can render `<MediaInputsGrid ... />` unconditionally.
 */
const MediaInputsGrid = ({
  mediaInputs,
  handlers,
  canvas,
  nativeMobile,
  deviceUploadOnly,
}: MediaInputsGridProps) => {
  const classes = useStyles();

  if (mediaInputs.length === 0) return null;

  return (
    <div className={classes.grid}>
      {mediaInputs.map((input, index) => {
        const isVideo = input.type === InputType.VIDEO;
        return (
          <UploadArea
            key={input.id}
            title={
              input.title ||
              getUploadTitle({
                inputType: input.type,
                inputCount: mediaInputs.length,
                index,
                allInputs: mediaInputs,
              })
            }
            accept={isVideo ? videoAccept : imageAccept}
            allowedExtensions={isVideo ? videoExtensions : imageExtensions}
            value={input.src}
            isLoading={input.isLoading}
            hasUploadError={input.hasUploadError}
            isVideo={isVideo}
            onUpload={(file) => handlers.handleMediaUpload(input.id, file)}
            onRemove={() => handlers.handleMediaRemove(input.id)}
            {...(!deviceUploadOnly
              ? {
                  onSelectFromDrive: () =>
                    handlers.handleSelectFromDrive(input.id, input.type),
                  onSelectFromCanvas: () => canvas.openSelector(input.id),
                }
              : {})}
            onSelectFromNativePicker={
              nativeMobile
                ? () =>
                    handlers.handleSelectFromNativePicker(input.id, input.type)
                : undefined
            }
            hasCanvasImages={!deviceUploadOnly && canvas.images.length > 0}
            compact={mediaInputs.length === 1}
          />
        );
      })}
    </div>
  );
};

export default MediaInputsGrid;
